import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ContentChildren,
  forwardRef,
  HostBinding,
  Input, OnDestroy,
  OnInit,
  Optional,
  ViewChild
} from '@angular/core';
import { DropdownGroupComponent } from './dropdown-group.component';
import { DropdownItemComponent } from './dropdown-item.component';
import { AbstractControl, ControlValueAccessor, FormControl, FormGroupDirective, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DropdownTriggerDirective } from './dropdown-trigger.directive';
import { BehaviorSubject, Subject } from 'rxjs';
import { LocalizationService } from '../localization/localization.service';
import dropdownLocalization from './dropdown.localization';

@Component({
  selector: 'mtg-dropdown',
  templateUrl: './dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownComponent implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit {
  private control: AbstractControl;
  private onTouch: () => void;
  private onChange: (value) => void;
  @Input()
  label: string;
  @Input()
  formControl: FormControl;
  @Input()
  formControlName: string;
  @Input()
  variant: 'outline' | 'button' | 'link' = 'outline';
  @Input()
  errorVisible = true;
  @Input()
  resetVisible = true;
  @Input()
  size: 'sm' | 'md' = 'md';
  @Input()
  fluid: boolean;
  @Input()
  withSearch: boolean;
  @Input()
  disabled: boolean;
  @Input()
  multi: boolean;

  @ContentChildren(DropdownGroupComponent)
  groups: DropdownGroupComponent[];
  @ContentChildren(DropdownItemComponent)
  items: DropdownItemComponent[];
  @ViewChild(DropdownTriggerDirective)
  dropdownTrigger: DropdownTriggerDirective;

  value: any;
  valueText: string;
  value$ = new Subject();
  search: string;
  search$ = new BehaviorSubject<string>(null);
  localization = dropdownLocalization;

  @HostBinding('class')
  get class() {
    const classList = ['dropdown'];
    const control = this.control || this.formControl;
    if (control && control.touched && !control.valid) {
      classList.push('dropdown--error');
    }
    if (this.valueText) {
      classList.push('dropdown--filled');
    }
    if (!this.label) {
      classList.push('dropdown--without-label');
    }
    if (this.dropdownTrigger && this.dropdownTrigger.isVisible) {
      classList.push('dropdown--opened');
    }
    if (this.size === 'sm') {
      classList.push('dropdown--size-sm');
    } else {
      classList.push('dropdown--size-md');
    }
    if (this.withSearch) {
      classList.push('dropdown--with-search');
    }
    if (this.disabled) {
      classList.push('dropdown--disabled');
    }
    return classList.join(' ');
  }

  constructor(
    @Optional() private parentFormGroup: FormGroupDirective,
    private localizationService: LocalizationService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    if (this.parentFormGroup) {
      this.control = this.formControl || this.parentFormGroup.control.get(this.formControlName);
    }
  }

  ngOnDestroy(): void {
    this.value$.complete();
  }

  ngAfterViewInit(): void {
    this.getValueText();
  }

  updateView(): void {
    this.cdr.markForCheck();
  }

  searchValue(value: string): void {
    this.search = value;
    this.search$.next(value);
  }

  writeValue(value: any): void {
    this.searchValue('');
    this.value = value;
    this.value$.next(value);
    this.getValueText();
  }

  getValueText(): void {
    if (!this.items || !this.groups) {
      return;
    }
    let value;
    this.items.forEach(item => {
      if (item.value === this.value) {
        this.valueText = item.viewValue;
        this.value = item.value;
        value = item.value;
      }
    });
    this.groups.forEach(group => {
      group.items.forEach(item => {
        if (item.value === this.value) {
          this.valueText = item.viewValue;
          this.value = item.value;
          value = item.value;
        }
      });
    });
    setTimeout(() => {
      if (value === undefined) {
        this.value = this.multi ? [] : undefined;
        this.valueText = '';
      }
      this.cdr.markForCheck();
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  changeValue(value: any): void {
    this.searchValue('');
    this.value = value;
    this.value$.next(value);
    this.search$.next('');
    if (this.onTouch) {
      this.onTouch();
    }
    if (this.onChange) {
      this.onChange(value);
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onItemClick(item: DropdownItemComponent) {
    let newValue = '';
    if (this.multi) {
      const currentValue = this.value;
      if (currentValue.indexOf(item.value) > -1) {
        currentValue.splice(this.value.indexOf(item.value), 1);
      } else {
        currentValue.push(item.value);
      }
      newValue = currentValue;
      this.valueText = newValue.length > 0 ? item.viewValue : '';
    } else {
      newValue = item.value;
      this.valueText = item.viewValue;
      this.dropdownTrigger.hideMenu();
    }
    this.changeValue(newValue);
    this.cdr.markForCheck();
  }

  getErrors() {
    const control = (this.control || this.formControl);
    let errors = {};
    if (control && control.touched && control.errors) {
      errors = control.errors;
    }
    return Object.keys(errors)
      .filter(k => errors[k])
      .reduce((acc, value) => {
        let error = this.localizationService.formErrorLocalize(value);
        if (!error) {
          return acc;
        }
        if (typeof errors[value] === 'object') {
          for (const errorKey of Object.keys(errors[value])) {
            error = error.replace(`{${errorKey}}`, errors[value][errorKey]);
          }
        }
        acc.push(error);
        return acc;
      }, []);
  }

  reset(): void {
    this.changeValue(this.multi ? [] : undefined);
    this.valueText = null;
    this.cdr.markForCheck();
  }
}
